import { getErrorMessage } from '../utils/errorHandler';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '../components/ui/tooltip';
import { 
  Plus, 
  CalendarDays,
  History,
  Minus,
  Edit2
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr, de, enUS } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Separate component to avoid babel plugin recursion issues
function AbsenceList({ absences, locale, t, noDataText, showStatus }) {
  if (!absences || absences.length === 0) {
    return <p className="text-xs text-muted-foreground">{noDataText}</p>;
  }
  
  return (
    <ul className="text-xs space-y-1">
      {absences.map((absence, idx) => (
        <li key={idx} className="flex items-center gap-1">
          {showStatus && (
            <span className={`w-2 h-2 rounded-full ${absence.status === 'approved' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
          )}
          <span>
            {format(parseISO(absence.start_date), 'dd/MM/yyyy', { locale })}
            {absence.start_date !== absence.end_date && ` → ${format(parseISO(absence.end_date), 'dd/MM/yyyy', { locale })}`}
            {absence.day_part && absence.day_part !== 'full_day' && ` (${t(absence.day_part)})`}
          </span>
        </li>
      ))}
    </ul>
  );
}

// Editable hours component with +/- buttons
function EditableHours({ value, onChange, label, canEdit, dayHours = 8.5 }) {
  const [isOpen, setIsOpen] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  
  const handleSave = () => {
    onChange(tempValue);
    setIsOpen(false);
  };
  
  const adjust = (delta) => {
    setTempValue(Math.max(0, tempValue + delta));
  };
  
  if (!canEdit) {
    return <span className="font-medium">{(value / dayHours).toFixed(1)}j ({value}h)</span>;
  }
  
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button className="font-medium hover:underline cursor-pointer flex items-center gap-1">
          {(value / dayHours).toFixed(1)}j ({value}h)
          <Edit2 className="w-3 h-3 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64" align="end">
        <div className="space-y-3">
          <Label>{label}</Label>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => adjust(-dayHours)}>
              <Minus className="w-4 h-4" />
            </Button>
            <Input 
              type="number" 
              value={tempValue} 
              onChange={(e) => setTempValue(parseFloat(e.target.value) || 0)}
              className="text-center"
            />
            <Button size="sm" variant="outline" onClick={() => adjust(dayHours)}>
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground text-center">
            = {(tempValue / dayHours).toFixed(1)} jours
          </p>
          <Button size="sm" className="w-full" onClick={handleSave}>
            Enregistrer
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function LeaveManagement() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const locale = language === 'de' ? de : language === 'en' ? enUS : fr;
  
  const [allocations, setAllocations] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1];
  const [newAllocation, setNewAllocation] = useState({
    user_id: '',
    year: currentYear,
    allocated_hours: 170
  });
  const [dayHours, setDayHours] = useState(8.5);
  
  // Check if user can edit (directeur or manager)
  const canEdit = user?.role === 'directeur' || user?.role === 'manager';

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings/public`);
      setDayHours(res.data.default_day_duration || 8.5);
    } catch (err) {
      console.error('Error fetching config:', err);
    }
  }, []);

  const fetchAllocations = useCallback(async () => {
    try {
      const params = { year: currentYear };
      // Collaborateurs ne voient que leurs propres données
      if (user?.role === 'collaborateur') {
        params.user_id = user.id;
      }
      const res = await axios.get(`${API}/leave-allocations`, { params });
      setAllocations(res.data);
    } catch (err) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  }, [currentYear, t, user]);

  const fetchTeam = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/team`);
      setTeam(res.data);
    } catch (err) {
      console.error('Error fetching team:', err);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
    fetchAllocations();
    if (canEdit) {
      fetchTeam();
    }
  }, [fetchConfig, fetchAllocations, fetchTeam, canEdit]);

  const handleSaveAllocation = async () => {
    try {
      await axios.post(`${API}/leave-allocations`, newAllocation);
      toast.success(t('allocationSaved'));
      setDialogOpen(false);
      fetchAllocations();
    } catch (err) {
      toast.error(getErrorMessage(err, t('error')));
    }
  };

  const handleUpdateAllocation = async (allocationId, field, value) => {
    try {
      // Check if it's a virtual allocation (needs to be created first)
      if (allocationId.startsWith('virtual_')) {
        const userId = allocationId.split('_')[1];
        // Create a new allocation first
        const createData = {
          user_id: userId,
          year: currentYear,
          allocated_hours: field === 'allocated_hours' ? value : 0,
        };
        await axios.post(`${API}/leave-allocations`, createData);
        toast.success(t('allocationSaved'));
        fetchAllocations();
      } else {
        // Only update allocated_hours via this endpoint
        await axios.put(`${API}/leave-allocations/${allocationId}`, { allocated_hours: value });
        toast.success(t('allocationSaved'));
        fetchAllocations();
      }
    } catch (err) {
      toast.error(getErrorMessage(err, t('error')));
    }
  };

  // Separate function for updating carryover (reliquat)
  const handleUpdateCarryover = async (userId, value) => {
    try {
      await axios.put(`${API}/leave-carryover/${userId}/${currentYear}`, { carryover_hours: value });
      toast.success(t('allocationSaved'));
      fetchAllocations();
    } catch (err) {
      toast.error(getErrorMessage(err, t('error')));
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await axios.get(`${API}/leave-allocations/export-pdf`, {
        params: { year: currentYear },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `conges_${currentYear}_${format(new Date(), 'yyyy-MM-dd_HHmm')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(t('pdfExported'));
    } catch (err) {
      toast.error(getErrorMessage(err, t('error')));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <TooltipProvider>
    <div className="space-y-6" data-testid="leave-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl">{t('leaveBalance').toUpperCase()}</h1>
          <p className="text-muted-foreground text-sm">
            {t('annualLeave')} {currentYear}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button 
              variant="outline"
              onClick={handleExportPDF}
              data-testid="export-leave-pdf-btn"
            >
              <CalendarDays className="h-4 w-4 mr-2" />
              {t('exportPDF')}
            </Button>
          )}
        </div>
      </div>

      {/* Leave Cards */}
      {allocations.length === 0 ? (
        <div className="border border-border rounded-lg p-8 text-center">
          <CalendarDays className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">{t('noDataAvailable')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {allocations.map((allocation) => {
            const userDayHours = allocation.user_day_hours || dayHours;
            const usedPercentage = allocation.allocated_hours > 0 ? (allocation.used_hours / allocation.allocated_hours) * 100 : 0;
            const plannedPercentage = allocation.allocated_hours > 0 ? (allocation.planned_hours / allocation.allocated_hours) * 100 : 0;
            const isVirtual = allocation.is_virtual;
            
            return (
              <div 
                key={allocation.id} 
                className={`border rounded-lg p-4 bg-card hover:border-primary transition-colors ${isVirtual ? 'border-dashed border-amber-300' : 'border-border'}`}
                data-testid={`leave-card-${allocation.user_id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-medium text-lg">{allocation.user_name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {currentYear} • {allocation.weekly_contractual_hours || 40}h/{t('week')}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold">{allocation.remaining_days || 0}{t('days')}</p>
                    <p className="text-xs text-muted-foreground">{t('daysRemaining')}</p>
                  </div>
                </div>

                {/* Virtual allocation warning */}
                {isVirtual && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded p-2 mb-4 text-xs text-amber-700 dark:text-amber-400">
                    {t('noAllocationYet')}
                  </div>
                )}

                {/* Progress Bar */}
                <div className="space-y-2 mb-4">
                  <div className="h-3 bg-muted rounded-full overflow-hidden flex">
                    <div 
                      className="bg-emerald-500 h-full transition-all"
                      style={{ width: `${Math.min(usedPercentage, 100)}%` }}
                      title={`${t('daysUsed')}: ${allocation.used_days || 0}j`}
                    />
                    <div 
                      className="bg-amber-500 h-full transition-all"
                      style={{ width: `${Math.min(plannedPercentage, 100 - usedPercentage)}%` }}
                      title={`${t('daysPlanned')}: ${allocation.planned_days || 0}j`}
                    />
                  </div>
                </div>

                {/* Details - Formule explicite du calcul (Anomalie 4) */}
                <div className="space-y-2 text-sm">
                  {/* Previous year balance - clickable to edit */}
                  <div className="flex justify-between bg-blue-50 dark:bg-blue-900/20 rounded px-2 py-1 -mx-2">
                    <span className="flex items-center gap-2 text-blue-700 dark:text-blue-400">
                      <History className="w-3 h-3" />
                      {t('prevYearCarryOver')}
                    </span>
                    <span className="text-blue-700 dark:text-blue-400">
                      <EditableHours 
                        value={allocation.prev_year_initial || 0}
                        onChange={(v) => handleUpdateCarryover(allocation.user_id, v)}
                        label={t('prevYearCarryOver')}
                        canEdit={canEdit}
                        dayHours={userDayHours}
                      />
                    </span>
                  </div>
                  
                  {/* Allocated hours - clickable to edit */}
                  <div className="flex justify-between">
                    <span className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-muted" />
                      + {t('daysAllocated')}
                    </span>
                    <EditableHours 
                      value={allocation.allocated_hours || 0}
                      onChange={(v) => handleUpdateAllocation(allocation.id, 'allocated_hours', v)}
                      label={t('daysAllocated')}
                      canEdit={canEdit}
                      dayHours={userDayHours}
                    />
                  </div>
                  
                  {/* Total disponible = Reliquat + Allocation */}
                  <div className="flex justify-between bg-slate-50 dark:bg-slate-900/20 rounded px-2 py-1 -mx-2 font-medium">
                    <span className="flex items-center gap-2 text-slate-700 dark:text-slate-400">
                      = {t('totalAvailable') || 'Total disponible'}
                    </span>
                    <span className="text-slate-700 dark:text-slate-400">
                      {((allocation.prev_year_initial || 0) + (allocation.allocated_hours || 0)) / userDayHours}{t('days')} ({(allocation.prev_year_initial || 0) + (allocation.allocated_hours || 0)}{t('h')})
                    </span>
                  </div>
                  
                  {/* Used hours with tooltip showing last 5 absences */}
                  <div className="flex justify-between">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="flex items-center gap-2 cursor-help">
                          <div className="w-3 h-3 rounded-full bg-emerald-500" />
                          - {t('daysUsed')}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="left" className="max-w-[250px]">
                        <p className="font-medium mb-1">{t('lastAbsences')}</p>
                        <AbsenceList absences={allocation.last_absences} locale={locale} t={t} noDataText={t('noAbsences')} />
                        {allocation.used_from_prev_year > 0 && (
                          <p className="text-xs text-blue-500 mt-2 pt-2 border-t">
                            {t('deductedFromPrevYear')}: {(allocation.used_from_prev_year / userDayHours).toFixed(1)}{t('days')}
                          </p>
                        )}
                      </TooltipContent>
                    </Tooltip>
                    <div className="text-right">
                      <span className="font-medium text-emerald-600">{allocation.used_days || 0}{t('days')} ({allocation.used_hours || 0}{t('h')})</span>
                      {allocation.used_from_prev_year > 0 && (
                        <div className="text-xs text-blue-500">
                          {t('fromN1')}: {(allocation.used_from_prev_year / userDayHours).toFixed(1)}{t('days')}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Planned days with tooltip showing next 5 absences */}
                  <div className="flex justify-between">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="flex items-center gap-2 cursor-help">
                          <div className="w-3 h-3 rounded-full bg-amber-500" />
                          - {t('daysPlanned')}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="left" className="max-w-[250px]">
                        <p className="font-medium mb-1">{t('nextAbsences')}</p>
                        <AbsenceList absences={allocation.next_absences} locale={locale} t={t} noDataText={t('noPlannedAbsences')} showStatus />
                      </TooltipContent>
                    </Tooltip>
                    <span className="font-medium text-amber-600">{allocation.planned_days || 0}{t('days')} ({allocation.planned_hours || 0}{t('h')})</span>
                  </div>
                  
                  <div className="flex justify-between border-t border-border pt-2 mt-2">
                    <span className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-primary" />
                      {t('daysRemaining')}
                    </span>
                    <span className={`font-bold ${(allocation.remaining_hours || 0) < 0 ? 'text-red-500' : 'text-primary'}`}>
                      {(allocation.remaining_days || 0).toFixed ? allocation.remaining_days.toFixed(1) : allocation.remaining_days || 0}{t('days')} ({typeof allocation.remaining_hours === 'number' ? allocation.remaining_hours.toFixed(1) : allocation.remaining_hours || 0}{t('h')})
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Allocate Leave Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('allocateLeave').toUpperCase()}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>{t('collaborator')}</Label>
              <Select
                value={newAllocation.user_id}
                onValueChange={(v) => setNewAllocation({ ...newAllocation, user_id: v })}
              >
                <SelectTrigger data-testid="allocation-user-select">
                  <SelectValue placeholder={t('collaborator')} />
                </SelectTrigger>
                <SelectContent>
                  {team.map(member => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.first_name} {member.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('year')}</Label>
              <Select
                value={newAllocation.year.toString()}
                onValueChange={(v) => setNewAllocation({ ...newAllocation, year: parseInt(v) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map(year => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('allocatedHours')}</Label>
              <Input
                type="number"
                value={newAllocation.allocated_hours}
                onChange={(e) => setNewAllocation({ ...newAllocation, allocated_hours: parseFloat(e.target.value) })}
                data-testid="allocation-hours-input"
              />
              <p className="text-xs text-muted-foreground">
                {(newAllocation.allocated_hours / dayHours).toFixed(1)} {t('days')} × {dayHours}h
              </p>
            </div>

            <Button onClick={handleSaveAllocation} className="w-full" data-testid="save-allocation-btn">
              {t('saveAllocation')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </TooltipProvider>
  );
}
